import React from 'react';
import { HeartPulse, Shield, CheckCircle2, ArrowRight, Phone, Star, Building2, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { hdfcPlans } from '../lib/policiesData';
import { useTranslation } from 'react-i18next';
import SEOHead, { ADVISOR_NAME, ADVISOR_PHONE } from '../components/SEOHead';
import { trackWhatsAppClick } from '../services/analytics';

const HealthInsuranceHub = () => {
  const { t, i18n } = useTranslation();
  const isTe = i18n.language === 'te';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': isTe ? 'హెచ్‌డిఎఫ్‌సి ఎర్గో ఆరోగ్య బీమా కేంద్రం | VSR Insurance Plus' : 'HDFC ERGO Health Insurance Plans in India | VSR Insurance Plus',
    'description': 'Compare HDFC ERGO retail health insurance plans: Optima Secure (4X Cover), Optima Restore, Optima Senior, and Super Top-Up with V. S. Reddy.',
    'provider': {
      '@type': 'InsuranceAgency',
      'name': 'VSR Insurance Plus'
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-20 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title={isTe ? "హెచ్‌డిఎఫ్‌సి ఎర్గో ఆరోగ్య బీమా | ఆప్టిమా సెక్యూర్ (4X కవరేజ్) | VSR Insurance Plus" : "HDFC ERGO Health Insurance Plans | Optima Secure (4X Cover) | VSR Insurance Plus"}
        description={isTe ? "హెచ్‌డిఎఫ్‌సి ఎర్గో సమగ్ర ఆరోగ్య బీమా: ఆప్టిమా సెక్యూర్ (4 రెట్ల కవరేజ్), ఆప్టిమా రిస్టోర్, మరియు వృద్ధ తల్లిదండ్రుల కోసం ఆప్టిమా సీనియర్. 14,000+ ఆసుపత్రులలో నగదు రహిత సేవలు." : "Comprehensive HDFC ERGO health plans: Optima Secure (4X multiplier), Optima Restore, and senior parent health insurance with zero room rent capping. Free consultation with V. S. Reddy."}
        canonicalPath="/health-insurance"
        schemaJson={pageSchema}
        breadcrumbs={[{ name: isTe ? 'ఆరోగ్య బీమా' : 'Health Insurance', path: '/health-insurance' }]}
      />

      <div className="container mx-auto px-6 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 p-2.5 px-4 bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <HeartPulse className="w-4 h-4" /> {isTe ? "14,000+ ఆసుపత్రులలో నగదు రహిత ఆరోగ్య రక్షణ" : "14,000+ Cashless Hospital Network"}
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-6 tracking-tight">
            {isTe ? "హెచ్‌డిఎఫ్‌సి ఎర్గో ఆరోగ్య బీమా పథకాలు" : "HDFC ERGO Health Insurance Plans"}
          </h1>
          <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            {isTe
              ? "వైద్య ద్రవ్యోల్బణం నుండి మీ కుటుంబాన్ని కాపాడుకోండి. హెచ్‌డిఎఫ్‌సి ఎర్గో ద్వారా రూమ్ రెంట్ పరిమితులు లేని చికిత్స, 4 రెట్ల కవరేజ్ మరియు 68 నాన్-మెడికల్ వస్తువులకు 100% పూర్తి చెల్లింపు."
              : "Medical inflation in India runs at 14% annually. HDFC ERGO health insurance provides cashless treatments, 4X coverage multipliers, and complete protection against non-medical hospital deductions."}
          </p>
        </div>

        {/* 3 Pillars of Health Security */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <Shield className="w-10 h-10 text-rose-500 mb-4" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {isTe ? "రూమ్ రెంట్‌పై ఎలాంటి కోతలు లేవు" : "Zero Room Rent Capping"}
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              {isTe
                ? "నచ్చిన డీలక్స్ రూమ్ ఎంచుకున్నా మీ ఆసుపత్రి బిల్లులలో ఎలాంటి దామాషా కోతలు (proportionate deductions) ఉండవు."
                : "No proportionate deductions. Stay in any single private or deluxe room without penalties on doctors' fees or surgery bills."}
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <Star className="w-10 h-10 text-amber-500 mb-4" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {isTe ? "4 రెట్ల కవరేజ్ మల్టిప్లైయర్" : "4X Coverage Multiplier"}
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              {isTe
                ? "ఆప్టిమా సెక్యూర్‌లో మొదటి రోజు నుండే డబుల్ కవరేజ్, రెండేళ్లలో 3 రెట్లు మరియు 100% ఆటోమేటిక్ రీస్టోర్ సదుపాయం."
                : "Optima Secure gives 2X cover from Day 1, 3X in 2 claim-free years, and 100% instant restoration for total peace of mind."}
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <Building2 className="w-10 h-10 text-emerald-500 mb-4" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {isTe ? "14,000+ క్యాష్‌లెlevel ఆసుపత్రులు" : "14,000+ Network Hospitals"}
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              {isTe
                ? "హైదరాబాద్ మరియు భారతదేశమంతటా ఉన్న అపోలో, యశోద, కేర్, కిమ్స్ వంటి ప్రముఖ ఆసుపత్రులలో 20 నిమిషాల్లో నగదు రహిత అనుమతులు."
                : "Cashless approvals processed in under 20 minutes across premier hospitals including Apollo, KIMS, Care, and Yashoda."}
            </p>
          </div>
        </div>

        {/* Featured Health Policies Grid */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white mb-3">
              {isTe ? "హెచ్‌డిఎఫ్‌సి ఎర్గో ఆరోగ్య బీమా పథకాలు" : "HDFC ERGO Health Insurance Plans"}
            </h2>
            <p className="text-slate-500 text-sm">
              {isTe ? "కుటుంబం మరియు వృద్ధ తల్లిదండ్రుల వైద్య రక్షణ కోసం రూపొందించిన పథకాలు." : "Compare plans designed for growing families, elderly parents, and pre-existing medical conditions."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {hdfcPlans.map(plan => (
              <div key={plan.id} className="bg-white dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-xl hover:border-rose-200 transition-all duration-300 flex flex-col justify-between">
                <div>
                  <span className="inline-block px-3 py-1 bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                    {isTe ? (plan.tagTe || plan.tag) : plan.tag}
                  </span>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                    {isTe ? (plan.nameTe || plan.name) : plan.name}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 leading-relaxed">
                    {isTe ? (plan.shortDescTe || plan.shortDesc) : plan.shortDesc}
                  </p>

                  <ul className="space-y-2.5 mb-6">
                    {(isTe && plan.featuresTe ? plan.featuresTe : plan.features).slice(0, 4).map((f, i) => (
                      <li key={i} className="flex gap-2 items-start text-xs text-slate-600 dark:text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <div>
                    <span className="text-[11px] text-slate-400 block font-medium">{t('cover_range')}</span>
                    <span className="text-sm font-bold text-slate-900 dark:text-white">
                      {isTe ? (plan.coverageTe || plan.coverage) : plan.coverage}
                    </span>
                  </div>
                  <Link
                    to={`/plan/${plan.id}`}
                    className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-sm font-semibold transition-colors flex items-center gap-1.5"
                  >
                    {t('details')} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Optima Secure Deep-Dive Spotlight */}
        <div className="bg-gradient-to-r from-rose-900 to-red-950 rounded-3xl p-8 md:p-12 text-white shadow-xl flex flex-col md:flex-row justify-between items-center gap-8 mb-16">
          <div className="max-w-xl">
            <span className="text-xs font-bold text-rose-300 uppercase tracking-widest block mb-2">
              {isTe ? "ఫ్లాగ్‌షిప్ హెల్త్ పాలసీ" : "Flagship Health Policy"}
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold mb-3">
              {isTe ? "హెచ్‌డిఎఫ్‌సి ఎర్గో ఆప్టిమా సెక్యూర్: 4 రెట్ల కవరేజ్" : "HDFC ERGO Optima Secure: 4X Coverage Explained"}
            </h2>
            <p className="text-rose-100 text-sm leading-relaxed mb-4">
              {isTe
                ? "మొదటి రోజు నుండే డబుల్ కవరేజ్, రెండేళ్లలో 3 రెట్లు, ఆటోమేటిక్ రిస్టోర్ మరియు 100% కన్స్యూమబుల్స్ చెల్లించే భారతదేశపు నంబర్ 1 హెల్త్ ఇన్సూరెన్స్."
                : "Learn why Optima Secure is rated India's top health insurance plan: Secure Benefit (2X from Day 1), Plus Benefit (3X in 2 years), Restore Benefit (100% refill), and Protect Benefit (covers 68 consumables)."}
            </p>
            <Link
              to="/blog/hdfc-ergo-optima-secure-plus-explained"
              className="inline-flex items-center gap-2 text-yellow-300 font-bold hover:underline text-sm"
            >
              {isTe ? "ఆప్టిమా సెక్యూర్ గైడ్ చదవండి" : "Read Full Optima Secure Guide"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="flex flex-col gap-3 w-full md:w-auto">
            <a
              href={`https://wa.me/919849664800?text=${encodeURIComponent(isTe ? 'నమస్కారం వి. ఎస్. రెడ్డి గారు, నేను హెచ్‌డిఎఫ్‌సి ఎర్గో ఆప్టిమా సెక్యూర్ హెల్త్ పాలసీ కొటేషన్ కోసం సంప్రదిస్తున్నాను.' : 'Hi V. S. Reddy, I would like to get a premium quote for HDFC ERGO Optima Secure for my family.')}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('health_hub_whatsapp')}
              className="px-8 py-3.5 bg-white text-rose-900 hover:bg-rose-50 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg transition-transform hover:-translate-y-0.5"
            >
              <Phone className="w-4 h-4 text-rose-600" /> {isTe ? "వాట్సాప్ కొటేషన్ పొందండి" : "WhatsApp Advisor for Quote"}
            </a>
            <Link
              to="/contact"
              className="px-8 py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium text-center border border-white/20 transition-colors text-sm"
            >
              {isTe ? "ఉచిత కన్సల్టేషన్ బుక్ చేయండి" : "Book Free Consultation"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthInsuranceHub;
